import { getLocale } from '../i18n/index.js';
import { buildAppComparisonHref } from '../domain/appComparisonLink.js';
import { buildDailyInsightTitle } from '../domain/dailyInsightTitle.js';
import { updatePageMetaTags } from './metaTags.js';

const DAILY_INSIGHT_LATEST_ENDPOINT = '/insights/daily/latest.json';
const MAX_CARD_SCREENSHOTS = 5;
let screenshotLightboxBound = false;
let screenshotLightboxDocument = null;

export function dailyInsightRoute(pathname = window.location.pathname) {
  const parts = String(pathname)
    .split('/')
    .filter(Boolean);
  const routeParts = ['zh', 'zh-cn', 'zh-hans', 'zh-hant', 'zh-tw', 'zh-hk', 'ja', 'jp', 'en'].includes((parts[0] || '').toLowerCase())
    ? parts.slice(1)
    : parts;
  if (routeParts[0] !== 'daily-insight') return { type: 'latest' };
  const date = routeParts[1];
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return { type: 'latest' };
  if (routeParts[2] === 'apps' && routeParts[3]) return { type: 'app', date, appId: routeParts[3] };
  return { type: 'report', date };
}

export function dailyInsightEndpoint(pathname = window.location.pathname) {
  const route = dailyInsightRoute(pathname);
  if (route.type === 'latest') return DAILY_INSIGHT_LATEST_ENDPOINT;
  return `/insights/daily/${route.date}.json`;
}

export async function loadDailyInsightReport(container, { fetchImpl = fetch } = {}) {
  if (!container) return;
  container.hidden = false;
  container.innerHTML = `<div class="daily-insight__notice">${isZh() ? '正在加载每日报告...' : 'Loading daily reports...'}</div>`;
  const route = dailyInsightRoute();
  try {
    const response = await fetchImpl(dailyInsightEndpoint(), { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    if (route.type === 'app') {
      renderDailyAppDetail(container, payload, route.appId, route.date);
    } else {
      renderDailyInsightReport(container, payload);
    }
  } catch (err) {
    container.innerHTML = `<div class="daily-insight__notice daily-insight__notice--error">${isZh() ? '每日报告加载失败' : 'Could not load daily reports'}：${escapeHtml(err.message ?? String(err))}</div>`;
  }
}

export function renderDailyInsightReport(container, report = {}) {
  if (!container) return;
  ensureScreenshotLightbox();
  container.hidden = false;
  const zh = isZh();
  const candidates = publicCandidates(report);
  // `date` is the data date: it drives data-file URLs and cross-links, so it must
  // stay unchanged to avoid 404s. `displayDateValue` is what the reader sees. On the
  // undated "latest" route we apply newspaper logic (edition date = data date + 1)
  // so a report generated at 21:00 yesterday reads as today's; dated archive routes
  // keep showing their own data date.
  const date = reportDate(report);
  const latestRoute = dailyInsightRoute().type === 'latest';
  const displayDateValue = latestRoute ? editionDate(date) : date;
  const title = reportTitle(report, zh, displayDateValue, candidates, latestRoute);
  const summary = reportSummary(report, zh, displayDateValue);
  updateDailyInsightMeta(title, summary, candidates);
  const cards = candidates.map((candidate) => publicCandidateCard(candidate, zh, date)).join('');
  const asOfLine = latestRoute
    ? `<p class="daily-insight__asof">${escapeHtml(dataAsOfNote(date, zh))}</p>`
    : '';
  container.innerHTML = `
    <article class="daily-insight">
      <header class="daily-insight__header">
        <div>
          <p class="eyebrow">Daily App Radar</p>
          <h1>${accentDailyTitle(title, zh, latestRoute)}</h1>
          <p class="daily-insight__lead">${escapeHtml(summary)}</p>
          <nav class="daily-crumbs" aria-label="${zh ? '面包屑' : 'Breadcrumb'}"><span>${escapeHtml(latestRoute ? latestDateLabel(displayDateValue, zh) : displayDate(date, zh))}</span></nav>
          ${asOfLine}
        </div>
      </header>
      <section class="daily-static__section">
        <h2>${zh ? 'Top 5 应用机会' : 'Top 5 app opportunities'}</h2>
        <div class="daily-insight__grid">
          ${cards || `<div class="daily-insight__notice">${zh ? '当前没有可展示的应用机会。' : 'No app opportunities are ready for this report.'}</div>`}
        </div>
      </section>
      <section class="daily-insight__meta">
        <strong>${zh ? '说明' : 'Note'}</strong>
        <span>${zh ? '公开页面只展示通用机会信号；内部目标市场和排序细则不在这里展示。' : 'Public pages show general opportunity signals only; internal market targeting and scoring details stay out of the public report.'}</span>
      </section>
    </article>
  `;
}

function accentDailyTitle(title, zh, latestRoute) {
  if (zh) return escapeHtml(title);
  const source = String(title ?? '');
  const segment = latestRoute ? 'Top 5 App Opportunities Today' : 'Top 5 App Opportunities';
  const index = source.indexOf(segment);
  if (index === -1) return escapeHtml(source);
  return `${escapeHtml(source.slice(0, index))}<span class="title-accent">${escapeHtml(segment)}</span>${escapeHtml(source.slice(index + segment.length))}`;
}

function renderDailyAppDetail(container, report = {}, appId, date = reportDate(report)) {
  ensureScreenshotLightbox();
  const zh = isZh();
  const candidate = publicCandidates(report).find((item) => String(item.trackId ?? '') === String(appId) || publicCandidateId(item) === String(appId));
  if (!candidate) {
    container.innerHTML = `<div class="daily-insight__notice daily-insight__notice--error">${zh ? '没有找到这个应用调研页。' : 'This app research page was not found.'}</div>`;
    return;
  }
  const screenshots = Array.isArray(candidate.screenshotUrls) ? candidate.screenshotUrls : [];
  const reviews = Array.isArray(candidate.reviewSamples) ? candidate.reviewSamples : [];
  const positiveReviews = reviews.filter((review) => Number(review.rating) >= 4).slice(0, 4);
  const negativeReviews = reviews.filter((review) => Number(review.rating) <= 2).slice(0, 4);
  container.innerHTML = `
    <article class="daily-app-detail">
      <nav class="daily-crumbs" aria-label="${zh ? '面包屑' : 'Breadcrumb'}"><a href="${zh ? `/zh/daily-insight/${date}/` : `/daily-insight/${date}/`}">${escapeHtml(displayDate(date, zh))}</a><span>${escapeHtml(candidate.name ?? 'Untitled app')}</span></nav>
      <header class="daily-app-detail__header">
        <div>
          <p class="eyebrow">Daily App Radar</p>
          <h1>${escapeHtml(candidate.name ?? 'Untitled app')}</h1>
          <p class="daily-app-detail__lead">${escapeHtml(zh ? candidate.appIntroZh : candidate.appIntroEn)}</p>
        </div>
        ${candidate.artworkUrl ? `<img class="daily-app-detail__icon" src="${escapeAttrUrl(candidate.artworkUrl)}" alt="">` : ''}
      </header>
      <section class="daily-app-detail__section">
        <h2>${zh ? '公开榜单信号' : 'Public chart signal'}</h2>
        <dl class="daily-insight-card__facts">
          <div><dt>${zh ? '源市场' : 'Source market'}</dt><dd>${escapeHtml(zh ? candidate.marketLabelZh : candidate.marketLabelEn)}</dd></div>
          <div><dt>${zh ? '榜单' : 'Chart'}</dt><dd>${escapeHtml(zh ? candidate.chartTypeZh : candidate.chartTypeEn)} #${numberText(candidate.currentRank)}</dd></div>
          <div><dt>${zh ? '评分量' : 'Rating count'}</dt><dd>${numberText(candidate.ratingCount)}${candidate.averageRating ? ` · ${Number(candidate.averageRating).toFixed(1)}/5` : ''}</dd></div>
          <div><dt>${zh ? '雷达分' : 'Radar score'}</dt><dd>${numberText(candidate.radarScore)}</dd></div>
        </dl>
      </section>
      <section class="daily-app-detail__section">
        <h2>${zh ? '应用说明' : 'App description'}</h2>
        <p>${escapeHtml(zh ? descriptionZh(candidate, 900) : descriptionPreview(candidate.storeDescription, 900))}</p>
      </section>
      <section class="daily-app-detail__section">
        <h2>${zh ? '截图' : 'Screenshots'}</h2>
        ${screenshots.length ? `<div class="daily-insight-card__screens">${screenshots.map((url, index) => screenshotImage(url, index, zh)).join('')}</div>` : `<p class="daily-insight-card__missing">${zh ? '本次没有抓到公开截图。' : 'No public screenshots were captured in this run.'}</p>`}
      </section>
      <section class="daily-app-detail__section">
        <h2>${zh ? '用户评论摘要' : 'Review summary'}</h2>
        <p>${escapeHtml(zh ? candidate.reviewSummaryZh : candidate.reviewSummaryEn)}</p>
        <p><strong>${zh ? '好评在说：' : 'Positive reviews mention: '}</strong>${escapeHtml(zh ? candidate.reviewPositiveZh : candidate.reviewPositiveEn)}</p>
        <p><strong>${zh ? '差评在说：' : 'Negative reviews mention: '}</strong>${escapeHtml(zh ? candidate.reviewNegativeZh : candidate.reviewNegativeEn)}</p>
        <p><strong>${zh ? '刷评风险：' : 'Review quality: '}</strong>${escapeHtml(zh ? candidate.reviewAuthenticityZh : candidate.reviewAuthenticityEn)}</p>
      </section>
      <section class="daily-app-detail__section">
        <h2>${zh ? '好评摘录' : 'Positive snippets'}</h2>
        ${renderReviewBlocks(positiveReviews, zh)}
      </section>
      <section class="daily-app-detail__section">
        <h2>${zh ? '差评摘录' : 'Negative snippets'}</h2>
        ${renderReviewBlocks(negativeReviews, zh)}
      </section>
      <section class="daily-app-detail__section">
        <h2>${zh ? '下一步' : 'Next check'}</h2>
        <p>${escapeHtml(zh ? candidate.nextZh : candidate.nextEn)}</p>
      </section>
    </article>
  `;
}

function publicCandidateCard(candidate = {}, zh, date) {
  const screenshots = Array.isArray(candidate.screenshotUrls) ? candidate.screenshotUrls.slice(0, MAX_CARD_SCREENSHOTS) : [];
  const detailPath = detailUrl(candidate, zh, date);
  const comparePath = compareUrl(candidate);
  const appName = candidate.name ?? 'Untitled app';
  return `
    <article class="daily-insight-card">
      <div class="daily-insight-card__topline">
        <span class="daily-insight-card__rank">#${numberText(candidate.rank)} ${zh ? '应用机会' : 'App opportunity'}</span>
        <span class="daily-insight-card__score">${zh ? '雷达分' : 'Radar score'} ${numberText(candidate.radarScore)}</span>
      </div>
      <div class="daily-insight-card__apphead">
        ${linkedIcon(candidate, comparePath)}
        <div>
          <h2>${comparePath ? `<a class="daily-insight-card__title-link" href="${escapeAttrUrlPath(comparePath)}">${escapeHtml(appName)}</a>` : escapeHtml(appName)}</h2>
          <p class="daily-insight-card__sub">${escapeHtml(zh ? candidate.appIntroZh : candidate.appIntroEn)}</p>
        </div>
      </div>
      <section class="daily-insight-card__brief">
        <div class="daily-insight-card__why">
          <strong>${zh ? '为什么这是机会' : 'Why this is an opportunity'}</strong>
          <p>${escapeHtml(zh ? candidate.reasonZh : candidate.reasonEn)}</p>
        </div>
        <dl class="daily-insight-card__evidence" aria-label="${zh ? '关键证据' : 'Key evidence'}">
          <div><dt>${zh ? '源市场' : 'Source market'}</dt><dd>${escapeHtml(zh ? candidate.marketLabelZh : candidate.marketLabelEn)} · ${escapeHtml(zh ? candidate.chartTypeZh : candidate.chartTypeEn)} #${numberText(candidate.currentRank)}</dd></div>
          <div><dt>${zh ? '评分量' : 'Rating count'}</dt><dd>${numberText(candidate.ratingCount)}${candidate.averageRating ? ` · ${Number(candidate.averageRating).toFixed(1)}/5` : ''}</dd></div>
          <div><dt>${zh ? '评论样本' : 'Review sample'}</dt><dd>${numberText(Array.isArray(candidate.reviewSamples) ? candidate.reviewSamples.length : 0)}</dd></div>
        </dl>
      </section>
      <section class="daily-insight-card__description">
        <strong>${zh ? '它具体做什么' : 'What it does'}</strong>
        <p>${escapeHtml(zh ? descriptionZh(candidate, 260) : descriptionEn(candidate, 260))}</p>
      </section>
      <div class="daily-insight-card__body">
        <div class="daily-insight-card__main">
          <div class="daily-insight-card__support">
            <section class="daily-insight-card__reviews">
              <strong>${zh ? '用户评论' : 'User reviews'}</strong>
              <p>${escapeHtml(zh ? candidate.reviewSummaryZh : candidate.reviewSummaryEn)}</p>
              <p><strong>${zh ? '刷评风险：' : 'Review quality: '}</strong>${escapeHtml(zh ? candidate.reviewAuthenticityZh : candidate.reviewAuthenticityEn)}</p>
              ${renderInlineReviewQuotes(candidate.reviewSamples, zh, 1, 160)}
            </section>
            <section class="daily-insight-card__checks">
              <div class="daily-insight-card__check daily-insight-card__check--risk">
                <strong>${zh ? '主要风险' : 'Main risk'}</strong>
                <p>${escapeHtml(zh ? candidate.riskZh : candidate.riskEn)}</p>
              </div>
              <div class="daily-insight-card__check daily-insight-card__check--next">
                <strong>${zh ? '下一步看什么' : 'What to check next'}</strong>
                <p>${escapeHtml(zh ? candidate.nextZh : candidate.nextEn)}</p>
              </div>
            </section>
          </div>
          <p><a class="daily-inline-link" href="${escapeAttrUrlPath(detailPath)}">${zh ? '查看完整调研页' : 'Open full research page'}</a></p>
        </div>
        <div class="daily-insight-card__media">
          ${screenshots.length ? `<div class="daily-insight-card__screens">${screenshots.map((url, index) => screenshotImage(url, index, zh)).join('')}</div>` : `<p class="daily-insight-card__missing">${zh ? '本次没有抓到公开截图。' : 'No public screenshots were captured in this run.'}</p>`}
        </div>
      </div>
    </article>
  `;
}

function publicCandidates(report = {}) {
  if (Array.isArray(report.publicCandidates)) return report.publicCandidates;
  if (Array.isArray(report.candidates)) return report.candidates;
  return [];
}

function reportDate(report = {}) {
  const fromUrl = String(report.reportUrl ?? '').match(/\/daily-insight\/(\d{4}-\d{2}-\d{2})\//)?.[1];
  if (fromUrl) return fromUrl;
  const generated = report.generatedAt ? new Date(report.generatedAt) : null;
  if (generated && !Number.isNaN(generated.getTime())) return generated.toISOString().slice(0, 10);
  return 'latest';
}

function reportTitle(report, zh, date, candidates, latest = false) {
  return buildDailyInsightTitle({
    locale: zh ? 'zh' : 'en',
    reportDate: date,
    candidates,
    noStrongOpportunity: report.noStrongOpportunity,
    latest,
  });
}

function reportSummary(report, zh, date) {
  if (!zh && report.summary) return report.summary;
  if (!publicCandidates(report).length) {
    return zh
      ? `${displayDate(date, zh)} 的原始雷达池仍有信号，但没有足够证据的应用机会；弱信号不会被包装成推荐。`
      : `The raw radar pool still has signals, but no app opportunity has enough evidence for publication today.`;
  }
  return zh
    ? `${displayDate(date, zh)} App Radar 报告：从公开榜单、商店页面、截图和近期评论中整理出的应用机会。`
    : `The latest App Radar report: five app opportunities ranked from public chart movement, store pages, screenshots, and recent reviews.`;
}

function updateDailyInsightMeta(title, summary, candidates) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const firstImage = (Array.isArray(candidates) ? candidates : [])
    .map((candidate) => candidate?.artworkUrl)
    .find((url) => typeof url === 'string' && /^https?:\/\//i.test(url));
  updatePageMetaTags({
    title,
    description: summary,
    url: window.location.href,
    image: firstImage ?? '',
  });
}

function detailUrl(candidate, zh, date) {
  const id = publicCandidateId(candidate);
  return zh ? `/zh/daily-insight/${date}/apps/${id}/` : `/daily-insight/${date}/apps/${id}/`;
}

function compareUrl(candidate) {
  const trackId = String(candidate?.trackId ?? '').trim();
  if (!/^\d+$/.test(trackId)) return '';
  return buildAppComparisonHref(trackId, candidate?.market);
}

function publicCandidateId(candidate) {
  return String(candidate?.trackId ?? candidate?.name ?? 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'unknown';
}

function descriptionZh(candidate = {}, max = 420) {
  const base = candidate.appIntroZh || '';
  const store = descriptionPreview(candidate.storeDescription, max);
  if (!store) return base || '当前没有可用简介，不能只凭名称判断用途。';
  return `${base} 机器翻译预览：${store}`;
}

function descriptionEn(candidate = {}, max = 420) {
  const base = candidate.appIntroEn || '';
  const store = descriptionPreview(candidate.storeDescription, max);
  // The English report must read as English. The store description is the raw
  // localized App Store copy, so only surface it when it is already Latin-script;
  // localized (CJK / Hangul / Cyrillic / …) copy is dropped rather than leaked
  // untranslated into the English "What it does" field.
  if (store && isPredominantlyLatin(store)) {
    return base ? `${base} ${store}` : store;
  }
  return base || 'No usable English summary in this run; judge the use case from screenshots and reviews.';
}

function isPredominantlyLatin(text) {
  const letters = String(text ?? '').match(/\p{L}/gu) || [];
  if (!letters.length) return false;
  const nonLatin = letters.filter((ch) => !/\p{Script=Latin}/u.test(ch)).length;
  return nonLatin / letters.length < 0.2;
}

function descriptionPreview(text, max = 420) {
  const cleaned = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.length > max ? `${cleaned.slice(0, max).trim()}...` : cleaned;
}

function renderInlineReviewQuotes(reviews, zh, limit = 2, max = 220) {
  const selected = Array.isArray(reviews) ? reviews.slice(0, limit) : [];
  if (!selected.length) return `<p class="daily-insight-card__missing">${zh ? '本次没有足够评论原文样本。' : 'No usable raw review quotes in this run.'}</p>`;
  return `<div class="daily-insight-card__quotes">${selected.map((review) => {
    const title = reviewTitle(review, zh);
    const content = reviewContent(review, zh);
    const label = reviewTranslationLabel(review, zh);
    return `<q>${escapeHtml(reviewQuotePreview(`${numberText(review.rating)}/5 ${title ? `${title}: ` : ''}${content}`, max))}${label}</q>`;
  }).join('')}</div>`;
}

function renderReviewBlocks(reviews, zh) {
  if (!reviews.length) return `<p class="muted">${zh ? '这组评论样本不足。' : 'Not enough samples in this group.'}</p>`;
  return reviews.map((review) => {
    const title = reviewTitle(review, zh) || (zh ? '无标题评论' : 'Untitled review');
    const content = reviewContent(review, zh);
    return `<blockquote><small>${numberText(review.rating)}/5 · ${escapeHtml(title)}${reviewTranslationLabel(review, zh)}</small>${escapeHtml(content)}</blockquote>`;
  }).join('');
}

function appIcon(candidate) {
  if (!candidate.artworkUrl) return '<div class="daily-insight-card__icon daily-insight-card__icon--empty" aria-hidden="true"></div>';
  return `<img class="daily-insight-card__icon" src="${escapeAttrUrl(candidate.artworkUrl)}" alt="" loading="eager" decoding="async">`;
}

function linkedIcon(candidate, comparePath) {
  const icon = appIcon(candidate);
  if (!comparePath) return icon;
  const name = candidate?.name ?? 'app';
  return `<a class="daily-insight-card__icon-link" href="${escapeAttrUrlPath(comparePath)}" aria-label="${escapeHtml(`Compare ${name} across storefronts`)}">${icon}</a>`;
}

function screenshotImage(url, index = 0, zh = false) {
  const safeUrl = escapeAttrUrl(url);
  if (!safeUrl) return '';
  const label = zh ? `放大截图 ${index + 1}` : `Open screenshot ${index + 1}`;
  return `<button type="button" class="daily-insight-card__screenshot-button" data-daily-screenshot-url="${safeUrl}" aria-label="${escapeHtml(label)}"><img src="${safeUrl}" alt="" loading="eager" decoding="async"></button>`;
}

function ensureScreenshotLightbox() {
  if (typeof document === 'undefined' || !document.body) return;
  if (!document.querySelector('[data-daily-screenshot-lightbox]')) {
    const lightbox = document.createElement('div');
    lightbox.className = 'daily-screenshot-lightbox';
    lightbox.hidden = true;
    lightbox.setAttribute('data-daily-screenshot-lightbox', '');
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'App Store screenshot');
    lightbox.innerHTML = `
      <button type="button" class="daily-screenshot-lightbox__close" data-daily-screenshot-close aria-label="Close screenshot">×</button>
      <img class="daily-screenshot-lightbox__image" alt="">
    `;
    document.body.append(lightbox);
  }
  if (screenshotLightboxBound && screenshotLightboxDocument === document) return;
  document.addEventListener('click', handleScreenshotLightboxClick);
  document.addEventListener('keydown', handleScreenshotLightboxKeydown);
  screenshotLightboxBound = true;
  screenshotLightboxDocument = document;
}

function handleScreenshotLightboxClick(event) {
  const target = event.target;
  const opener = target?.closest?.('[data-daily-screenshot-url]');
  if (opener) {
    event.preventDefault();
    openScreenshotLightbox(opener.getAttribute('data-daily-screenshot-url'));
    return;
  }
  if (target?.closest?.('[data-daily-screenshot-close]') || target?.matches?.('[data-daily-screenshot-lightbox]')) {
    closeScreenshotLightbox();
  }
}

function handleScreenshotLightboxKeydown(event) {
  if (event.key === 'Escape') closeScreenshotLightbox();
}

function openScreenshotLightbox(url) {
  const lightbox = document.querySelector('[data-daily-screenshot-lightbox]');
  const image = lightbox?.querySelector('.daily-screenshot-lightbox__image');
  if (!lightbox || !image || !/^https?:\/\//i.test(String(url ?? ''))) return;
  image.setAttribute('src', url);
  lightbox.hidden = false;
  document.body.classList.add('daily-screenshot-lightbox-open');
  lightbox.querySelector('[data-daily-screenshot-close]')?.focus();
}

function closeScreenshotLightbox() {
  const lightbox = document.querySelector('[data-daily-screenshot-lightbox]');
  const image = lightbox?.querySelector('.daily-screenshot-lightbox__image');
  if (!lightbox) return;
  lightbox.hidden = true;
  image?.removeAttribute('src');
  document.body.classList.remove('daily-screenshot-lightbox-open');
}

function reviewTitle(review, zh) {
  return String((zh ? review?.title : review?.titleEn || review?.title) ?? '').trim();
}

function reviewContent(review, zh) {
  return String((zh ? review?.content : review?.contentEn || review?.content) ?? '').trim();
}

function reviewTranslationLabel(review, zh) {
  if (zh || !review?.translationSource || !review?.translationSourceLanguage || review.translationSourceLanguage === 'en') return '';
  return `<span class="daily-insight-card__translation">Machine translated from ${escapeHtml(String(review.translationSourceLanguage).toUpperCase())}</span>`;
}

function reviewQuotePreview(value, max = 220) {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1).trim()}...` : text;
}

function displayDate(date, zh) {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat(zh ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

function latestDateLabel(date, zh) {
  return zh ? `今天 · ${displayDate(date, zh)}` : `Today · ${displayDate(date, zh)}`;
}

// Newspaper edition date: the data pipeline runs at ~21:00 (Sydney) and stamps the
// report with that day's date, so during the following day the freshest edition is
// the one built from yesterday's cut. Shift the DISPLAY date forward by one day so the
// reader sees "today". Only valid YYYY-MM-DD input is shifted; anything else (e.g.
// 'latest') is returned untouched. Data files, cross-links and windows keep the data date.
function editionDate(date) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date ?? ''));
  if (!match) return date;
  const next = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1));
  if (Number.isNaN(next.getTime())) return date;
  return next.toISOString().slice(0, 10);
}

// Honest data-freshness note: the edition date is one day ahead of the data, so we
// state the actual data cutoff (the data date at 21:00 Sydney) alongside it.
function dataAsOfNote(date, zh) {
  return zh
    ? `数据截至 ${displayDate(date, zh)} 21:00（悉尼）`
    : `Data as of ${displayDate(date, zh)}, 21:00 (Sydney)`;
}

function isZh() {
  return getLocale().startsWith('zh');
}

function numberText(value) {
  return Number.isFinite(Number(value)) ? String(value) : '0';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttrUrl(value) {
  const url = String(value ?? '');
  if (!/^https?:\/\//i.test(url)) return '';
  return escapeHtml(url);
}

function escapeAttrUrlPath(value) {
  const path = String(value ?? '');
  if (!path.startsWith('/')) return '/daily-insight/';
  return escapeHtml(path);
}
